import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ChevronRight, Play, Music, Shirt, Car, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import BeatPlayer from "../components/BeatPlayer";
import TrendingProducts from "../components/TrendingProducts";
import RecommendedBeats from "../components/RecommendedBeats";
import VibeOnboarding from "../components/VibeOnboarding";
import SEO from "../components/SEO";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredBeats, setFeaturedBeats] = useState([]);
  const [featuredMerch, setFeaturedMerch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { user } = useAuth();

  const handleShowOnboarding = useCallback(() => {
    if (user && !localStorage.getItem("onboarding_skipped")) {
      setShowOnboarding(true);
    }
  }, [user]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [beatsRes, merchRes] = await Promise.all([
          axios.get(`${API}/beats`),
          axios.get(`${API}/merch`)
        ]);
        setFeaturedBeats(beatsRes.data.slice(0, 4));
        setFeaturedMerch(merchRes.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO title="Premium Beats, Merch & Culture" description="Premium beats, exclusive merch, and car culture. Fuel your creativity with high-octane sounds and streetwear." />
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1752070522773-dbf492cc52c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxuZW9uJTIwdG9reW8lMjBzdHJlZXQlMjBuaWdodCUyMGN5YmVycHVua3xlbnwwfHx8fDE3NzE3MTY0OTl8MA&ixlib=rb-4.1.0&q=85"
            alt="Neon Tokyo"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020204] via-[#020204]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-neon-cyan/10 via-transparent to-neon-magenta/10" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 mb-6 uppercase tracking-widest">
              Welcome to the
            </Badge>
            <h1 className="font-heading text-6xl md:text-8xl lg:text-9xl font-black uppercase tracking-tighter mb-6">
              <span className="text-glow-cyan">Gas</span>{" "}
              <span className="text-glow-magenta">Station</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 font-light">
              Premium beats, exclusive merch, and car culture. 
              Fuel your creativity with our selection of high-octane sounds and streetwear.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/beats">
                <Button
                  size="lg"
                  className="bg-neon-cyan text-black font-bold uppercase tracking-widest px-8 py-6 btn-clip neon-cyan hover:scale-105 transition-transform"
                  data-testid="hero-beats-button"
                >
                  <Music className="w-5 h-5 mr-2" />
                  Browse Beats
                </Button>
              </Link>
              <Link to="/merch">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 hover:border-neon-magenta hover:text-neon-magenta uppercase tracking-widest px-8 py-6"
                  data-testid="hero-merch-button"
                >
                  <Shirt className="w-5 h-5 mr-2" />
                  Shop Merch
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-neon-cyan rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass p-8 card-hover"
            >
              <div className="w-14 h-14 bg-neon-cyan/20 flex items-center justify-center mb-6">
                <Music className="w-7 h-7 text-neon-cyan" />
              </div>
              <h3 className="font-heading text-xl uppercase tracking-wide mb-3">Premium Beats</h3>
              <p className="text-zinc-400 font-light">
                High-quality instrumentals with multiple licensing options. From basic leases to exclusive rights.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass p-8 card-hover"
            >
              <div className="w-14 h-14 bg-neon-magenta/20 flex items-center justify-center mb-6">
                <Shirt className="w-7 h-7 text-neon-magenta" />
              </div>
              <h3 className="font-heading text-xl uppercase tracking-wide mb-3">Exclusive Merch</h3>
              <p className="text-zinc-400 font-light">
                Limited edition streetwear and accessories. Hoodies, tees, phone cases, and more.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass p-8 card-hover"
            >
              <div className="w-14 h-14 bg-neon-green/20 flex items-center justify-center mb-6">
                <Car className="w-7 h-7 text-neon-green" />
              </div>
              <h3 className="font-heading text-xl uppercase tracking-wide mb-3">Car Culture</h3>
              <p className="text-zinc-400 font-light">
                JDM meets hip-hop. Car meet clips, build showcases, and automotive content.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trending Products Section */}
      <TrendingProducts />

      {/* Recommended For You */}
      <section className="py-16 px-6" data-testid="homepage-recommendations">
        <div className="max-w-7xl mx-auto">
          <RecommendedBeats limit={6} onShowOnboarding={handleShowOnboarding} />
        </div>
      </section>

      {/* Vibe Onboarding Modal */}
      {showOnboarding && <VibeOnboarding onComplete={handleOnboardingComplete} />}

      {/* Featured Beats */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-neon-cyan/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 mb-3">
                Hot Tracks
              </Badge>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight">
                Featured Beats
              </h2>
            </div>
            <Link to="/beats">
              <Button variant="ghost" className="text-neon-cyan hover:bg-neon-cyan/10" data-testid="view-all-beats">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-zinc-900/50 animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {featuredBeats.map((beat, index) => (
                <motion.div
                  key={beat.beat_id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <BeatPlayer beat={beat} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Merch */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <Badge className="bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50 mb-3">
                Fresh Drops
              </Badge>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight">
                Latest Merch
              </h2>
            </div>
            <Link to="/merch">
              <Button variant="ghost" className="text-neon-magenta hover:bg-neon-magenta/10" data-testid="view-all-merch">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-zinc-900/50 animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {featuredMerch.map((item, index) => (
                <motion.div
                  key={item.merch_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/merch/${item.merch_id}`}>
                    <div className="group relative aspect-[3/4] overflow-hidden bg-zinc-900 border border-white/5 card-hover">
                      <img
                        src={item.images[0] || ""}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-neon-cyan font-mono">${item.price.toFixed(2)}</p>
                      </div>
                      {/* Quick add button on hover */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                        <Button className="bg-neon-magenta text-black font-bold uppercase" data-testid={`quick-view-${item.merch_id}`}>
                          Quick View
                        </Button>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-neon-cyan/20 via-neon-magenta/20 to-neon-green/20 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <Zap className="w-16 h-16 text-neon-cyan mx-auto mb-6" />
          <h2 className="font-heading text-4xl md:text-6xl uppercase tracking-tight mb-6">
            Ready to Fuel Up?
          </h2>
          <p className="text-lg text-zinc-400 mb-10 max-w-2xl mx-auto">
            Get exclusive access to new beat drops, limited merch releases, and car culture content.
          </p>
          <Link to="/beats">
            <Button
              size="lg"
              className="bg-neon-cyan text-black font-bold uppercase tracking-widest px-12 py-6 btn-clip neon-cyan animate-pulse-glow"
              data-testid="cta-button"
            >
              Start Shopping
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-neon-cyan flex items-center justify-center">
                <span className="text-black font-heading font-bold text-sm">GS</span>
              </div>
              <span className="font-heading text-sm uppercase tracking-widest">Gas Station</span>
            </div>
            <div className="flex gap-8 text-sm text-zinc-500">
              <Link to="/beats" className="hover:text-neon-cyan transition-colors">Beats</Link>
              <Link to="/merch" className="hover:text-neon-magenta transition-colors">Merch</Link>
              <Link to="/garage" className="hover:text-neon-green transition-colors">Garage</Link>
            </div>
            <p className="text-sm text-zinc-600">
              &copy; {new Date().getFullYear()} Gas Station. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
